/**
 * RequestInviteForm
 *
 * Story 14: Request Invite Form (Self-Service)
 *
 * Features:
 * - Company name input
 * - Contact email input
 * - Contact phone input
 * - County selection
 * - Service type multi-select
 * - Form validation with Zod
 * - Success/error feedback
 */

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useRequestInvite } from '../hooks/useRequestInvite';

// California counties in Central Coast region
const CALIFORNIA_COUNTIES = [
  'San Luis Obispo',
  'Santa Barbara',
  'Monterey',
  'Kern',
  'San Benito',
  'Ventura',
  'Kings',
  'Tulare',
] as const;

// Service types offered
const SERVICE_TYPES = [
  'Roll-off Dumpsters',
  'Residential Collection',
  'Commercial Collection',
  'Recycling Services',
  'Hazardous Waste',
  'Green Waste',
] as const;

// Zod validation schema
const requestInviteSchema = z.object({
  companyName: z.string().min(2, 'Company name must be at least 2 characters'),
  email: z.string().email('Invalid email address'),
  phone: z.string().min(10, 'Valid phone number is required'),
  county: z.string().min(1, 'Please select a county'),
  serviceTypes: z.array(z.string()).min(1, 'Select at least one service type'),
});

type RequestInviteFormData = z.infer<typeof requestInviteSchema>;

export function RequestInviteForm() {
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<RequestInviteFormData>({
    resolver: zodResolver(requestInviteSchema),
    defaultValues: {
      companyName: '',
      email: '',
      phone: '',
      county: '',
      serviceTypes: [],
    },
  });

  const { mutateAsync: submitRequest, isPending } = useRequestInvite();

  const onSubmit = async (data: RequestInviteFormData) => {
    try {
      setErrorMessage(null);
      await submitRequest(data);
      setSubmitted(true);
      reset();
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to submit request');
      console.error('Request submission error:', error);
    }
  };

  // Success state
  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto py-8 px-4">
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-neutral-900 mb-2">
              Request Submitted Successfully!
            </h2>
            <p className="text-neutral-600 mb-6">
              Thank you for your interest in joining CalWMP. We will review your request and send
              you an invite code soon.
            </p>
            <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-primary-800">
                <strong>What happens next?</strong>
              </p>
              <ul className="text-sm text-primary-700 mt-2 space-y-1 text-left">
                <li>• Our team will review your application</li>
                <li>• You&apos;ll receive an invite code via email</li>
                <li>• Use the code to complete your registration</li>
                <li>• Start accepting jobs in your service area</li>
              </ul>
            </div>
            <button
              type="button"
              onClick={() => setSubmitted(false)}
              className="btn-secondary"
            >
              Submit Another Request
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Form state
  return (
    <div className="max-w-2xl mx-auto py-8 px-4">
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-neutral-900 mb-2">Request an Invite Code</h2>
          <p className="text-neutral-600">
            Join the CalWMP service provider network. Fill out the form below and we&apos;ll send you
            an invite code to get started.
          </p>
        </div>

        {errorMessage && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800">{errorMessage}</p>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Company Name */}
          <div>
            <label htmlFor="companyName" className="block text-sm font-medium text-neutral-700 mb-1">
              Company Name <span className="text-red-500">*</span>
            </label>
            <input
              id="companyName"
              type="text"
              {...register('companyName')}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                errors.companyName ? 'border-red-500' : 'border-neutral-300'
              }`}
              placeholder="ABC Hauling Services"
            />
            {errors.companyName && (
              <p className="mt-1 text-sm text-red-600">{errors.companyName.message}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-neutral-700 mb-1">
              Contact Email <span className="text-red-500">*</span>
            </label>
            <input
              id="email"
              type="email"
              {...register('email')}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                errors.email ? 'border-red-500' : 'border-neutral-300'
              }`}
              placeholder="contact@abchauling.com"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
            )}
          </div>

          {/* Phone */}
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-neutral-700 mb-1">
              Contact Phone <span className="text-red-500">*</span>
            </label>
            <input
              id="phone"
              type="tel"
              {...register('phone')}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                errors.phone ? 'border-red-500' : 'border-neutral-300'
              }`}
              placeholder="(555) 123-4567"
            />
            {errors.phone && (
              <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
            )}
          </div>

          {/* County Selection */}
          <div>
            <label htmlFor="county" className="block text-sm font-medium text-neutral-700 mb-1">
              Primary Service County <span className="text-red-500">*</span>
            </label>
            <select
              id="county"
              {...register('county')}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                errors.county ? 'border-red-500' : 'border-neutral-300'
              }`}
            >
              <option value="">Select a county</option>
              {CALIFORNIA_COUNTIES.map((county) => (
                <option key={county} value={county}>
                  {county}
                </option>
              ))}
            </select>
            {errors.county && (
              <p className="mt-1 text-sm text-red-600">{errors.county.message}</p>
            )}
          </div>

          {/* Service Types */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Service Types <span className="text-red-500">*</span>
            </label>
            <div className="space-y-2">
              {SERVICE_TYPES.map((serviceType) => (
                <label
                  key={serviceType}
                  className="flex items-center space-x-2 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    value={serviceType}
                    {...register('serviceTypes')}
                    className="w-4 h-4 text-primary-600 border-neutral-300 rounded focus:ring-primary-500"
                  />
                  <span className="text-sm text-neutral-700">{serviceType}</span>
                </label>
              ))}
            </div>
            {errors.serviceTypes && (
              <p className="mt-1 text-sm text-red-600">{errors.serviceTypes.message}</p>
            )}
          </div>

          {/* Submit Button */}
          <div className="pt-4">
            <button
              type="submit"
              disabled={isPending}
              className={`w-full px-6 py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors ${
                isPending ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isPending ? 'Submitting...' : 'Request Invite Code'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
